import { Card, Panel, ConfirmModal } from '@/components/Bicester';
import Image from '@/components/Bicester/Image';
import { Form } from '@/components/formik-rsuite';
import { FormikContextType, FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Alert, IconButton } from 'rsuite';
import { useModal } from '@ebay/nice-modal-react';
import LogList from './LogList';
import useRouter from '@/hooks/useRouter';
import { useRequest } from 'ahooks';
import * as Yup from 'yup';
import { LEVEL_TYPE, SERVICE_STATUS_TYPE, SERVICE_TYPE } from '@/constants';
import { getPathname } from '@/utils/history';
import { ACCOUNT_TABS } from '@/views/CustomerAccount';
import _ from 'lodash';

interface ServiceInfoProps {
  /**
   * 操作表单的loading
   * - 修改、取消、删除、添加
   */
  formLoading?: boolean;
  /**
   * 表单基础数据的loading
   */
  basicLoading?: boolean;
  basicInfoTitle?: string;
  /**
   * 基础信息
   * - formik
   * - infoData 详情数据
   * - isEdit 编辑状态
   */
  basicInfo?: ({
    formik,
    infoData,
    isEdit,
  }: {
    formik: FormikContextType<any>;
    infoData: any;
    isEdit: boolean;
  }) => React.ReactNode;
  serviceInfoTitle?: string;
  /**
   * 服务信息
   * - formik
   * - infoData 详情数据
   * - isEdit 编辑状态
   */
  serviceInfo: ({
    formik,
    infoData,
    isEdit,
  }: {
    formik: FormikContextType<any>;
    infoData: any;
    isEdit: boolean;
  }) => React.ReactNode;
  /**
   * 第三板块信息
   * - formik
   * - infoData 详情数据
   * - isEdit 编辑状态
   */
  thirdCardInfo?: ({
    formik,
    infoData,
    isEdit,
  }: {
    formik: FormikContextType<any>;
    infoData: any;
    isEdit: boolean;
  }) => React.ReactNode;
  /**
   * 服务详情板块底部toolbar
   * - formik
   */
  infoBottomToolbar?: ({
    service_id,
    infoData,
    isEdit,
    onReload,
  }: {
    service_id: number;
    infoData: any;
    isEdit: boolean;
    /**
     * 重新加载页面数据
     */
    onReload?: () => void;
  }) => React.ReactNode;
  /**
   * 服务详情表单默认值
   */
  defaultServiceInfo: any;
  /**
   * 服务详情表单校验规则
   */
  serviceSchema: Yup.ObjectSchema<any>;
  /**
   * 获取服务详情信息的fetch
   */
  fetchInfo: (service_id: number | string) => Promise<any>;
  /**
   * 编辑服务详情的fetch
   */
  fetchEdit: (data: any) => Promise<any>;
  /**
   * 获取表单默认值
   */
  getDefaultInfo: (data: any) => any;
  /**
   * 提交表单前的操作
   */
  handleBeforeSubmit?: (formik: FormikContextType<any>, values: any) => boolean;
  /**
   * 获取提交保存时的data
   */
  getSubmitData: (id: number | string, values: any) => any;
}

const ServiceInfo = ({
  formLoading = false,
  basicLoading = false,
  basicInfoTitle = '基础信息',
  basicInfo,
  serviceInfoTitle = '服务信息',
  serviceInfo,
  thirdCardInfo,
  infoBottomToolbar,
  defaultServiceInfo,
  serviceSchema,
  fetchInfo,
  fetchEdit,
  getDefaultInfo,
  handleBeforeSubmit,
  getSubmitData,
}: ServiceInfoProps) => {
  const { match } = useRouter<{ id: string }>();
  const modal = useModal(ConfirmModal);

  const [isEdit, setIsEdit] = useState(false);
  const [defaultInfo, setDefaultInfo] = useState<any>(defaultServiceInfo);

  const {
    run: getInfo,
    data: infoData,
  } = useRequest(fetchInfo, {
    manual: true,
  });

  const { run: addEdit } = useRequest(fetchEdit, {
    manual: true,
  });

  const formik = useFormik({
    initialValues: defaultInfo,
    validationSchema: serviceSchema,
    enableReinitialize: true, // initialValues默认值受控
    onSubmit(values, actions) {
      if (formik.isValid && (_.isNil(handleBeforeSubmit) || handleBeforeSubmit(formik, values))) {
        modal.show({
          contentText: '保存本次服务记录修改吗？',
          submitText: '确认保存',
          cancelText: '暂不',
          onSubmit() {
            const data = getSubmitData(Number(match.params.id), values);
            addEdit(data).then((res) => {
              if (res?.id) {
                const { id } = res;
                setIsEdit(false);
                Alert.success(res?.msg ?? '保存成功');
                fetchNewInfo(id);
              }
            });
          },
        });
      }
    },
  });

  const fetchNewInfo = (serviceId: string | number) => {
    getInfo(serviceId).then((res) => {
      const defaultValues = getDefaultInfo(res);
      formik.setValues(defaultValues);
      setDefaultInfo(defaultValues);
    });
  };

  useEffect(() => {
    fetchNewInfo(match.params.id);
  }, []);

  return (
    <>
      <FormikProvider value={formik}>
        <Form guard>
          <Panel
            title="服务详情"
            toolbar={
              <div className="flex justify-end w-full">
                <IconButton
                  className="flex items-center mr-[30px]"
                  disabled={infoData?.status === SERVICE_STATUS_TYPE.CANCELED} // 已取消的服务无法编辑
                  onClick={() => {
                    if (isEdit) {
                      if (formik.dirty) {
                        modal.show({
                          contentText: '信息还没保存，确定离开吗？',
                          submitText: '确定',
                          cancelText: '取消',
                          onSubmit: () => {
                            formik.resetForm();
                            setIsEdit(!isEdit);
                          },
                        });
                      } else {
                        formik.resetForm();
                        setIsEdit(!isEdit);
                      }
                    } else {
                      if (
                        [
                          SERVICE_TYPE.SHOPPING,
                          SERVICE_TYPE.PARKING,
                          SERVICE_TYPE.SA,
                          SERVICE_TYPE.LOUNGE,
                        ].includes(infoData?.type) &&
                        infoData?.status !== SERVICE_STATUS_TYPE.FINISHED
                      ) {
                        Alert.error('该服务还没完成，不能修改');
                      } else {
                        setIsEdit(!isEdit);
                      }
                    }
                  }}
                >
                  <Image src="/resources/images/edit.svg" />
                  <div className="text-[12px] text-[#999999] ml-[5px]">
                    {isEdit ? '取消' : '编辑'}
                  </div>
                </IconButton>
                <IconButton
                  type="submit"
                  className="flex items-center"
                  disabled={!isEdit}
                  onClick={() => {
                    formik.submitForm();
                  }}
                >
                  <Image src="/resources/images/save.svg" />
                  <div className="text-[12px] text-[#999999] ml-[5px]">保存</div>
                </IconButton>
              </div>
            }
          >
            <div className="flex">
              <div className="flex-1 flex flex-col mr-[74px]">
                <Card title={basicInfoTitle}>
                  {basicInfo ? (
                    basicInfo({ formik, infoData, isEdit })
                  ) : (
                    <>
                      {infoData?.is_subscribe === 1 && ( // is_subscribe 是否预约 0否 1是
                        <Card.Input
                          notEditable
                          label="服务编号"
                          formKey="service_no"
                          value={infoData?.service_no}
                        />
                      )}
                      <Card.Input
                        notEditable
                        label="服务地点"
                        formKey="servicesite_name"
                        value={infoData?.servicesite_name}
                      />
                      <Card.Input
                        notEditable
                        label="服务创建人"
                        formKey="creator_name"
                        value={infoData?.creator_name || '客户创建'}
                      />
                      <Card.Input
                        notEditable
                        label="客户名"
                        formKey="user_name"
                        value={infoData?.user_name}
                        autoWidth
                        hasBtn={Boolean(infoData?.member_id || infoData?.not_member_id)}
                        customBtn={
                          <IconButton
                            className="flex-1"
                            onClick={() => {
                              const isMember = infoData?.level !== LEVEL_TYPE.NOT;
                              window.open(
                                getPathname(
                                  `/customer-account/${
                                    isMember ? ACCOUNT_TABS.MEMBER : ACCOUNT_TABS.NO_MEMBER
                                  }/detail/${
                                    isMember ? infoData?.member_id : infoData?.not_member_id
                                  }`
                                )
                              );
                            }}
                          >
                            <Image
                              src={`/resources/images/user${
                                infoData?.sex === '男'
                                  ? '-man'
                                  : infoData?.sex === '女'
                                  ? '-woman'
                                  : ''
                              }.svg`}
                            />
                          </IconButton>
                        }
                      />
                      <Card.Input
                        notEditable
                        label="会员等级"
                        formKey="level"
                        inputType="level"
                        value={infoData?.level}
                      />
                      <Card.Input
                        notEditable
                        label="手机号"
                        formKey="phone"
                        value={infoData?.phone}
                      />
                      {infoData?.is_subscribe === 1 && ( // is_subscribe 是否预约 0否 1是
                        <>
                          <Card.Picker
                            notEditable
                            label="服务是否预约"
                            formKey="is_subscribe"
                            pickerType="TorF"
                            value={infoData?.is_subscribe}
                          />
                          <Card.DatePicker
                            notEditable
                            label="预约日期"
                            formKey="subscribe_date"
                            cleanable={false}
                            value={infoData?.subscribe_date}
                          />
                          <Card.Picker
                            notEditable
                            label="预约方式"
                            formKey="subscribe_style"
                            pickerType="TorF"
                            value={infoData?.subscribe_style}
                          />
                          <Card.Input
                            notEditable
                            label="预约备注"
                            formKey="remark"
                            inputType="textarea"
                            value={infoData?.remark}
                          />
                        </>
                      )}
                    </>
                  )}
                </Card>
                {thirdCardInfo?.({ formik, infoData, isEdit })}
              </div>
              <Card title={serviceInfoTitle}>
                <Card.Input
                  notEditable
                  label="服务项目"
                  formKey="type_name"
                  value={infoData?.type_name}
                />
                {serviceInfo({ formik, infoData, isEdit })}
              </Card>
            </div>
            {infoBottomToolbar?.({
              service_id: Number(match.params.id),
              infoData,
              isEdit,
              onReload: () => fetchNewInfo(match.params.id),
            })}
          </Panel>
        </Form>
      </FormikProvider>
      <LogList infoData={infoData} />
    </>
  );
};

export default ServiceInfo;
